import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useConversations, { useProjectConversations } from 'store/useConversationStore';
import { useChat } from 'store/useChatStore';
import { useProjects } from 'hooks/useProjects';
import { useAppDispatch } from 'redux/hooks';
import {
	setVisibleProjects,
	setConversations,
	setError,
	setActiveProjectConversationList,
} from 'features/conversation-slice';
import { Tooltip } from 'components/atoms/Base/Logo/Tooltip';
import {
	FolderClosed,
	FolderOpen,
	MoreHorizontal,
	MoreVertical,
	PanelLeft,
	PanelRight,
	FolderPlus,
	Search,
	SquarePen,
	Table2,
	Trash2,
	Edit2,
	Check,
	X,
} from 'lucide-react';
import { useAiChatApiService } from 'api/providers/AppServiceProvider';
import { ConversationProjectRequest } from 'api/services/ai-chat-api-service';
import { PrimaryRoutes } from 'common/enums/internal-route-enums';

interface AIConversationProps {
	onSidebarCollapsedChange?: (collapsed: boolean) => void;
	onNewChatClicked?: (clicked: boolean) => void;
}

function AIConversation({ onSidebarCollapsedChange, onNewChatClicked }: AIConversationProps) {
	// Custom scrollbar styles
	const scrollbarStyles = `
		.custom-scrollbar {
			scrollbar-width: thin;
			scrollbar-color: #c1c1c1 #F5F8FA; 
			scrollbar-gutter: stable;
		}
		.custom-scrollbar::-webkit-scrollbar {
			width: 8px;
			-webkit-appearance: none;
			display: block;
		}
		.custom-scrollbar::-webkit-scrollbar-track {
			background: #F5F8FA; 
			border-radius: 4px;
			margin: 2px;
		}
		.custom-scrollbar::-webkit-scrollbar-thumb {
			background: #c1c1c1;
			border-radius: 4px;
			border: 1px solid #ffffff; 
			min-height: 40px;
		}
		.custom-scrollbar::-webkit-scrollbar-thumb:hover {
			background: #a8a8a8;
		}
		.custom-scrollbar::-webkit-scrollbar-thumb:active {
			background: #888;
		}
		.custom-scrollbar::-webkit-scrollbar-corner {
			background: #ffffff; 
		}
		@media (min-width: 1024px) {
			.custom-scrollbar::-webkit-scrollbar {
				width: 10px;
			}
		}
		@media (max-width: 768px) {
			.custom-scrollbar::-webkit-scrollbar {
				width: 6px;
			}
		}
		@media (max-width: 480px) {
			.custom-scrollbar::-webkit-scrollbar {
				width: 4px;
			}
		}
		/* Chat history scrollable area - responsive */
		.chat-history-scroll {
			height: calc(100vh - 320px);
			max-height: calc(100vh - 320px);
			overflow-y: auto;
			scrollbar-gutter: stable;
		}
		@media (max-height: 800px) {
			.chat-history-scroll {
				height: calc(100vh - 280px);
				max-height: calc(100vh - 280px);
			}
		}
		@media (max-height: 600px) {
			.chat-history-scroll {
				height: calc(100vh - 200px);
				max-height: calc(100vh - 200px);
			}
		}
		@media (max-height: 500px) {
			.chat-history-scroll {
				height: calc(100vh - 150px);
				max-height: calc(100vh - 150px);
			}
		}
		@media (max-height: 400px) {
			.chat-history-scroll {
				height: calc(100vh - 100px);
				max-height: calc(100vh - 100px);
			}
		}
	`;

	const dispatch = useAppDispatch();
	const { loadConversation, activeConversationId, startNewConversation } = useChat();
	const {
		visibleProjects,
		loadingProjects,
		activeProject,
		hasMoreProjects,
		projects,
		projectsLoaded,
		createProject,
		loadProjects,
		loadMore,
		selectProject,
		renameProject,
		deleteProject,
	} = useProjects();
	// Get all conversations (for when no project is selected)
	const {
		conversations: allConversations,
		selectConversation,
		loadDataExplorer,
		loadMoreConversations,
		currentPage,
		hasMore,
		loading,
		isLoadingMore,
		deleteConversationById,
	} = useConversations(true);

	// Get project conversations
	const {
		activeProjectConversationList,
		loadingProjectConversations,
		fetchProjectConversations,
		clearProjectConversationList,
	} = useProjectConversations();
	const [searchTerm, setSearchTerm] = useState('');
	const [showCollapseTooltip, setShowCollapseTooltip] = useState(false);
	const [newProjectName, setNewProjectName] = useState('');
	const [showNewProjectInput, setShowNewProjectInput] = useState(false);
	const [draggedConversationId, setDraggedConversationId] = useState<string | null>(null);
	const [dragOverProjectId, setDragOverProjectId] = useState<string | null>(null);
	const [isAssigning, setIsAssigning] = useState(false);
	// 3-dots menu and dialogs state
	const [showProjectMenu, setShowProjectMenu] = useState<string | null>(null);
	const [showConversationMenu, setShowConversationMenu] = useState<string | null>(null);
	const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
		type: 'project' | 'conversation';
		id: string;
		name: string;
	} | null>(null);
	const [editingProject, setEditingProject] = useState<{ id: string; name: string } | null>(null);
	const [tempProjectName, setTempProjectName] = useState('');
	const [activeProjectConversationId, setActiveProjectConversationId] = useState<string | null>(null);
	const [conversationSource, setConversationSource] = useState<'main' | 'project' | null>(null);
	const [showAllProjectConversations, setShowAllProjectConversations] = useState(false);
	const [showSearchInput, setShowSearchInput] = useState(false);
	const [isSearching, setIsSearching] = useState(false);
	const [hasSearchedBefore, setHasSearchedBefore] = useState(false);
	const chatRef = useRef<HTMLDivElement>(null);
	const [collapsed, setChatPanelCollapsed] = useState(false);
	const [isMobileView, setIsMobileView] = useState(false);
	const [mobileHidden, setMobileHidden] = useState(false);
	const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const aiChatApiService = useAiChatApiService();
	const navigate = useNavigate();

	// Removed manual fetchConversations call - now handled by useConversations hook with autoLoad=true

	const handleSelectConversation = (id: string) => {
		selectConversation(id);
		loadConversation(id);
		// Clear active project and project conversation selection when selecting from main chat history
		selectProject(null);
		clearProjectConversationList();
		setActiveProjectConversationId(null);
		setConversationSource('main');
		// Update URL with conversation ID
		navigate(PrimaryRoutes.AIAnalystConversation.replace(':conversationId', id), { replace: true });
		// Close sidebar on mobile after action
		if (isMobileView) {
		onSidebarCollapsedChange?.(true);
	}
	};

	const handleSelectProjectConversation = (id: string) => {
		selectConversation(id);
		loadConversation(id);
		// Set the active project conversation ID and clear main chat selection
		setActiveProjectConversationId(id);
		setConversationSource('project');
		// Keep project folder open when selecting from project conversations
		// Update URL with conversation ID
		navigate(PrimaryRoutes.AIAnalystConversation.replace(':conversationId', id), { replace: true });

		// Close sidebar on mobile after action
		if (isMobileView) {
			onSidebarCollapsedChange?.(true);
		}
	};

	const handleStartNewConversation = () => {
		startNewConversation();
		onNewChatClicked?.(true);
		navigate(PrimaryRoutes.AIAnalyst, { replace: true });
		// Close sidebar on mobile after action
		if (isMobileView) {
		onSidebarCollapsedChange?.(true);
	}
	};

	const handleLoadMore = useCallback(() => {
		if (hasMore && !loading && !isLoadingMore) {
			console.log('Loading more conversations - Page:', currentPage + 1);
			loadMoreConversations(currentPage + 1);
		}
	}, [hasMore, loading, isLoadingMore, currentPage, loadMoreConversations]);

	// Project-related functions
	const handleCreateProject = useCallback(async () => {
		if (!newProjectName.trim()) return;

		try {
			await createProject(newProjectName.trim());
			setNewProjectName('');
			setShowNewProjectInput(false);
		} catch (error) {
			console.error('Failed to create project:', error);
		}
	}, [newProjectName, createProject]);

	const handleProjectSelect = useCallback(
		(project: any) => {
			selectProject(project);
			// Clear previous project conversation selection when switching projects
			setActiveProjectConversationId(null);
			setConversationSource(null);
			setShowAllProjectConversations(false); // Reset show all state when switching projects
			// Fetch conversations for the selected project
			if (project?.projectId) {
				fetchProjectConversations(project.projectId);
			}
		},
		[selectProject, fetchProjectConversations]
	);

	const handleShowAllProjectConversations = useCallback(() => {
		setShowAllProjectConversations(true);
	}, []);

	const handleShowLessProjectConversations = useCallback(() => {
		setShowAllProjectConversations(false);
	}, []);

	const performSearch = useCallback(
		async (searchValue: string) => {
			setIsSearching(true);
			setHasSearchedBefore(true); // Mark that user has searched before
			try {
				// Trigger a new search with the filter term
				const result = await aiChatApiService.getConversation(1, 20, searchValue.trim());

				// Update the conversations list with search results
				if (result && result.conversations) {
					// Update the store with search results - use the conversations directly
					dispatch(
						setConversations({
							conversations: result.conversations,
							total_count: result.total_count || result.conversations.length,
							page_size: result.page_size || 20,
							page: result.page || 1,
						})
					);
				}
			} catch (error) {
				console.error('Search failed:', error);
				dispatch(setError((error as Error).message));
			} finally {
				setIsSearching(false);
			}
		},
		[aiChatApiService, dispatch]
	);

	const clearSearch = useCallback(async () => {
		setIsSearching(true);
		try {
			// Clear search and reload all conversations
			const result = await aiChatApiService.getConversation(1, 20); // No filter term

			if (result && result.conversations) {
				// Update the store with all conversations - use the conversations directly
				dispatch(
					setConversations({
						conversations: result.conversations,
						total_count: result.total_count || result.conversations.length,
						page_size: result.page_size || 20,
						page: result.page || 1,
					})
				);
			}
		} catch (error) {
			console.error('Clear search failed:', error);
			dispatch(setError((error as Error).message));
		} finally {
			setIsSearching(false);
		}
	}, [aiChatApiService, dispatch]);

	const handleSearchToggle = useCallback(() => {
		setShowSearchInput(!showSearchInput);
		if (!showSearchInput) {
			// Clear any pending search timeout when closing search
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}
			setSearchTerm('');
			// Only clear search and reload conversations if user has searched before
			if (hasSearchedBefore) {
				clearSearch();
			}
		}
	}, [showSearchInput, clearSearch, hasSearchedBefore]);

	const handleSearchSubmit = useCallback(() => {
		// Clear any existing timeout
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}

		// Only perform search if there's actually a search term
		if (searchTerm.trim()) {
			performSearch(searchTerm);
		} else {
			// If no search term, just clear the search (reload all conversations)
			clearSearch();
		}
	}, [searchTerm, performSearch, clearSearch]);

	const handleSearchInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			setSearchTerm(value);

			// Clear any existing timeout
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}

			// If search term is empty, clear search immediately
			if (!value.trim()) {
				clearSearch();
				return;
			}

			// Set a new timeout for debounced search
			searchTimeoutRef.current = setTimeout(() => {
				performSearch(value);
			}, 1000); // 1000ms delay
		},
		[performSearch, clearSearch]
	);

	const handleSearchKeyPress = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter') {
				// Clear timeout and search immediately on Enter
				if (searchTimeoutRef.current) {
					clearTimeout(searchTimeoutRef.current);
				}
				handleSearchSubmit();
			} else if (e.key === 'Escape') {
				// Clear timeout and close search
				if (searchTimeoutRef.current) {
					clearTimeout(searchTimeoutRef.current);
				}
				setShowSearchInput(false);
				setSearchTerm('');
				// Clear search and reload all conversations
				clearSearch();
			}
		},
		[handleSearchSubmit, clearSearch]
	);

	// Use Redux pagination handlers
	const handleShowMoreProjects = useCallback(() => {
		loadMore();
	}, [loadMore]);

	const handleShowLessProjects = useCallback(() => {
		// Reset to first 2 projects
		dispatch(setVisibleProjects(projects.slice(0, 2)));
	}, [dispatch, projects]);

	// Drag and drop handlers
	const handleConversationDragStart = useCallback((e: React.DragEvent, conversationId: string) => {
		setDraggedConversationId(conversationId);
		e.dataTransfer.effectAllowed = 'move';
		e.dataTransfer.setData('text/plain', conversationId);
	}, []);

	const handleConversationDragEnd = useCallback(() => {
		setDraggedConversationId(null);
		setDragOverProjectId(null);
		// Note: We don't restore the previous project selection here because
		// the user might have intentionally opened a different project during drag
		// The project will remain open for the user to see
	}, []);

	const handleProjectDragOver = useCallback(
		(e: React.DragEvent, projectId: string) => {
			e.preventDefault();
			e.dataTransfer.dropEffect = 'move';
			setDragOverProjectId(projectId);

			// Only auto-open folder if it's not already open and we're not in the middle of a drop operation
			if (activeProject?.projectId !== projectId && projectId && !isAssigning) {
				// Find the project and set it as active temporarily
				const project = projects.find((p) => p.projectId === projectId);
				if (project) {
					selectProject(project);
					// Fetch conversations for the project if not already loaded
					fetchProjectConversations(projectId);
				}
			}
		},
		[activeProject?.projectId, projects, selectProject, fetchProjectConversations, isAssigning]
	);

	const handleProjectDragLeave = useCallback((e: React.DragEvent) => {
		// Only clear if we're actually leaving the project area
		if (!e.currentTarget.contains(e.relatedTarget as Node)) {
			setDragOverProjectId(null);
		}
	}, []);

	// 3-dots menu handlers
	const handleProjectMenuClick = useCallback(
		(e: React.MouseEvent, projectId: string) => {
			e.stopPropagation();
			setShowProjectMenu(showProjectMenu === projectId ? null : projectId);
		},
		[showProjectMenu]
	);

	const handleDeleteProject = useCallback(async (projectId: string, projectName: string) => {
		setDeleteConfirmDialog({ type: 'project', id: projectId, name: projectName });
		setShowProjectMenu(null);
	}, []);

	const handleRenameProject = useCallback((projectId: string, currentName: string) => {
		setEditingProject({ id: projectId, name: currentName });
		setTempProjectName(currentName);
		setShowProjectMenu(null);
	}, []);

	const confirmDelete = useCallback(async () => {
		if (!deleteConfirmDialog) return;

		const { type, id } = deleteConfirmDialog;
		try {
			if (type === 'project') {
				await deleteProject(id);
			} else if (type === 'conversation') {
				await deleteConversationById(id);
			}
			// The item will be removed from Redux state automatically
			setDeleteConfirmDialog(null);
		} catch (error) {
			console.error(`Failed to delete ${type}:`, error);
			alert(`Failed to delete ${type}: ${(error as Error).message}`);
		}
	}, [deleteConfirmDialog, deleteProject, deleteConversationById]);

	const handleRenameSubmit = useCallback(async () => {
		if (!editingProject || !tempProjectName.trim()) return;

		try {
			await renameProject(editingProject.id, tempProjectName.trim());
			// The project will be renamed in Redux state automatically
			setEditingProject(null);
			setTempProjectName('');
		} catch (error) {
			console.error('Failed to rename project:', error);
			alert(`Failed to rename project: ${(error as Error).message}`);
		}
	}, [editingProject, tempProjectName, renameProject]);

	const handleRenameCancel = useCallback(() => {
		setEditingProject(null);
		setTempProjectName('');
	}, []);

	// Conversation menu handlers
	const handleConversationMenuClick = useCallback(
		(e: React.MouseEvent, conversationId: string) => {
			e.stopPropagation();
			setShowConversationMenu(showConversationMenu === conversationId ? null : conversationId);
		},
		[showConversationMenu]
	);

	const handleDeleteConversation = useCallback(async (conversationId: string, conversationTitle: string) => {
		setDeleteConfirmDialog({ type: 'conversation', id: conversationId, name: conversationTitle });
		setShowConversationMenu(null);
	}, []);

	// Close menus when clicking outside
	const handleClickOutside = useCallback((e: MouseEvent) => {
		const target = e.target as Element;
		if (
			!target.closest('.project-menu') &&
			!target.closest('.conversation-menu') &&
			!target.closest('[data-menu-trigger]')
		) {
			setShowProjectMenu(null);
			setShowConversationMenu(null);
		}
	}, []);

	const handleProjectDrop = useCallback(
		async (e: React.DragEvent, projectId: string) => {
			e.preventDefault();
			e.stopPropagation();

			const conversationId = e.dataTransfer.getData('text/plain');

			if (!conversationId || !projectId) {
				return;
			}

			if (conversationId && projectId && !isAssigning) {
				try {
					setIsAssigning(true);
					const request: ConversationProjectRequest = {
						projectId,
						conversationId,
					};

					await aiChatApiService.assignConversationToProject(request);

					// Update local state immediately - add conversation to project conversations
					const conversation = allConversations.find((conv) => {
						const convId = conv.conversation_id;
						return convId === conversationId;
					});

					if (conversation && activeProjectConversationList) {
						// Add the conversation to the active project conversation list immediately
						const updatedProjectConversations = [...activeProjectConversationList, conversation];

						// Update the project conversations in Redux state immediately
						dispatch(setActiveProjectConversationList(updatedProjectConversations));
					}
				} catch (error) {
					console.error('Failed to assign conversation to project:', error);
				} finally {
					setIsAssigning(false);
				}
			}

			setDraggedConversationId(null);
			setDragOverProjectId(null);
		},
		[isAssigning, aiChatApiService, allConversations, activeProjectConversationList, dispatch]
	);

	const handleScroll = useCallback(
		(event: React.UIEvent<HTMLDivElement>) => {
			const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

			// Clear any existing timeout
			if (scrollTimeoutRef.current) {
				clearTimeout(scrollTimeoutRef.current);
			}

			// Throttle scroll events and only trigger when near bottom (90% threshold)
			scrollTimeoutRef.current = setTimeout(() => {
				const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
				if (scrollPercentage >= 0.9 && hasMore && !loading && !isLoadingMore) {
					handleLoadMore();
				}
			}, 500); // Increased throttle to 500ms for better performance
		},
		[hasMore, loading, isLoadingMore, handleLoadMore]
	);

	// Cleanup timeouts on unmount
	useEffect(() => {
		return () => {
			if (scrollTimeoutRef.current) {
				clearTimeout(scrollTimeoutRef.current);
			}
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}
		};
	}, []);

	// Load projects on component mount - only if not already loaded
	useEffect(() => {
		if (!projectsLoaded && !loadingProjects) {
			loadProjects();
		}
	}, [loadProjects, projectsLoaded, loadingProjects]);

	// Global click listener to close menus when clicking outside
	useEffect(() => {
		document.addEventListener('click', handleClickOutside);
		return () => document.removeEventListener('click', handleClickOutside);
	}, [handleClickOutside]);

	// Calculate scroll area height dynamically
	useEffect(() => {
		const calculateHeight = () => {
			// Height calculation is now handled by CSS
		};

		calculateHeight();
		window.addEventListener('resize', calculateHeight);

		return () => window.removeEventListener('resize', calculateHeight);
	}, []);

	// Use visibleProjects directly (Redux slice handles pagination)
	const projectsToShow = visibleProjects;

	// Always use the main conversation list for the chat history section
	// Note: When search is active, the API should return filtered results
	// For now, we'll use client-side filtering as fallback
	const filteredConversations = allConversations.filter(
		(conversation: any) => !searchTerm || conversation.title.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const sortedConversations = [...filteredConversations].sort((a: any, b: any) => {
		const aDate = 'updated_at' in a ? a.updated_at : a.lastUpdated;
		const bDate = 'updated_at' in b ? b.updated_at : b.lastUpdated;
		return new Date(bDate).getTime() - new Date(aDate).getTime();
	});

	const onViewSchemaStructure = () => {
		loadDataExplorer();

		// Close sidebar on mobile after action
		if (isMobileView) {
			onSidebarCollapsedChange?.(true);
		}
	};

	// Removed unused mouse event handlers

	useEffect(() => {
		const checkMobileView = () => {
			setIsMobileView(window.innerWidth < 768); // md breakpoint
		};
		
		checkMobileView();
		window.addEventListener('resize', checkMobileView);
		
		return () => window.removeEventListener('resize', checkMobileView);
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			setMobileHidden(false);
		}
		}, [isMobileView]);

	const handleChatPanelToggle = () => {
		const newCollapsedState = !collapsed;
		setChatPanelCollapsed(newCollapsedState);
		setShowCollapseTooltip(false); // Hide tooltip on click

		// Notify parent component about the collapsed state change
		onSidebarCollapsedChange?.(newCollapsedState);
	};

	const handleMobileClose = () => {
	if (isMobileView) {
		setMobileHidden(true);      
		onSidebarCollapsedChange?.(true);
	}
	};

	useEffect(() => {
	console.log({ isMobileView, mobileHidden });
	}, [isMobileView, mobileHidden]);



	return (
		<>
			<style>{scrollbarStyles}</style>
			<div
				className={`fixed top-0 bg-white transition-transform z-[50] duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]
					${
					isMobileView
						? mobileHidden
						? 'translate-x-full'
						: 'translate-x-0 w-[260px]'
						: collapsed
						? 'w-[60px]'
						: 'w-[260px]'
					}
				`}
				>

				<div
					ref={chatRef}
					className={`bg-[#F5F8FA] z-[50] flex flex-col h-screen overflow-y-auto transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] md:border-r md:border-tertiary-background md:shadow-[0_1px_0_rgba(200,212,234,0.3)] ${
						collapsed ? 'w-[60px]' : 'w-[260px]'
					}`}
					style={{
						boxSizing: 'border-box',
					}}
				>
					{/* Spacer to match Logo height with consistent padding */}
					<div className="h-20 relative py-2 px-3">
						<div className='md:hidden lg:hidden'>
							<button
								onClick={handleMobileClose}
								className="p-2 rounded-full hover:bg-tertiary-background transition"
							>
								<X className="w-[20px] h-[20px] text-secondary-text" strokeWidth={1.75} />
							</button>
						</div>

						{/* Collapse Button - Using Tooltip Component */}
						<div
							className={`absolute transform -translate-y-1/2 md:flex hidden
							${collapsed ? 'top-1/2 left-1/2 -translate-x-1/2' : 'top-[calc(50%-3px)] right-[calc(16px+4px)]'}
							`}
						>
							<Tooltip
								text={collapsed ? 'Open chat panel' : 'Close chat panel'}
								position="right"
								show={showCollapseTooltip}
							>
								<button
									onClick={handleChatPanelToggle}
									onMouseEnter={() => setShowCollapseTooltip(true)}
									onMouseLeave={() => setShowCollapseTooltip(false)}
									className="p-2 flex items-center justify-center hover:bg-tertiary-background rounded-[40px] transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
								>
									{collapsed ? (
										<PanelRight className="w-[20px] h-[20px] text-light-grey " strokeWidth={1.75} />
									) : (
										<PanelLeft className="w-[20px] h-[20px] text-light-grey" strokeWidth={1.75} />
									)}
								</button>
							</Tooltip>
						</div>
					</div>

					{/* Chat Actions - Aligned with sidebar navigation */}
					<div
						className={`${
							collapsed ? 'space-y-2 items-center' : 'gap-1 items-start px-0'
						} px-3 pt-3 transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] relative flex flex-col items-center`}
					>
						{/* New Chat - Using Tooltip Component */}
						<div className={`relative ${collapsed ? 'flex justify-center' : ''}`}>
							{collapsed ? (
								<Tooltip text="New chat" position="right">
									<button
										onClick={handleStartNewConversation}
										className={`group relative flex items-center transition-colors duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] ${
											collapsed
												? 'w-9 h-9 rounded-[40px] justify-center p-2'
												: 'w-[230px] h-[36px] px-2 rounded-[40px] text-left'
										} text-secondary-text hover:bg-tertiary-background`}
									>
										{/* Icon with consistent positioning */}
										<div
											className={`absolute top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center ${
												collapsed ? 'left-1/2 -translate-x-1/2' : 'left-2'
											}`}
										>
											<span className="text-primary-text">
												<SquarePen className="w-[20px] h-[20px]" strokeWidth={1.75} />
											</span>
										</div>

										{/* Text label with smooth width and opacity transition */}
										<div
											className={`overflow-hidden transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] ${
												collapsed ? 'w-0 opacity-0' : 'w-auto opacity-100 ml-[30px]'
											}`}
										>
											<span className="text-ui whitespace-nowrap">New chat</span>
										</div>
									</button>
								</Tooltip>
							) : (
								<button
									onClick={handleStartNewConversation}
									className={`group relative flex items-center transition-colors duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] ${
										collapsed
											? 'w-9 h-9 rounded-[40px] justify-center p-2'
											: 'w-[230px] h-[36px] px-2 rounded-[40px] text-left'
									} text-secondary-text hover:bg-tertiary-background`}
								>
									{/* Icon with consistent positioning */}
									<div
										className={`absolute top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center ${
											collapsed ? 'left-1/2 -translate-x-1/2' : 'left-2'
										}`}
									>
										<span className="text-primary-text">
											<SquarePen className="w-[20px] h-[20px]" strokeWidth={1.75} />
										</span>
									</div>

									{/* Text label with smooth width and opacity transition */}
									<div
										className={`overflow-hidden transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] ${
											collapsed ? 'w-0 opacity-0' : 'w-auto opacity-100 ml-[30px]'
										}`}
									>
										<span className="text-ui whitespace-nowrap">New chat</span>
									</div>
								</button>
							)}
						</div>

						{/* Search Chats - Using Tooltip Component */}
						<div className={`relative ${collapsed ? 'flex justify-center' : ''}`}>
							{!showSearchInput ? (
								<>
									{collapsed ? (
										<Tooltip text="Search chats" position="right">
											<button
												onClick={handleSearchToggle}
												className={`group relative flex items-center transition-colors duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] ${
													collapsed
														? 'w-9 h-9 rounded-[40px] justify-center p-2'
														: 'w-[230px] h-[36px] px-2 rounded-[40px] text-left'
												} text-secondary-text hover:bg-tertiary-background`}
											>
												{/* Icon with consistent positioning */}
												<div
													className={`absolute top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center ${
														collapsed ? 'left-1/2 -translate-x-1/2' : 'left-2'
													}`}
												>
													<span className="text-primary-text">
														<Search className="w-[20px] h-[20px]" strokeWidth={1.75} />
													</span>
												</div>

												{/* Text label with smooth width and opacity transition */}
												<div
													className={`overflow-hidden transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] ${
														collapsed ? 'w-0 opacity-0' : 'w-auto opacity-100 ml-[30px]'
													}`}
												>
													<span className="text-ui whitespace-nowrap">Search chats</span>
												</div>
											</button>
										</Tooltip>
									) : (
										<button
											onClick={handleSearchToggle}
											className={`group relative flex items-center transition-colors duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] ${
												collapsed
													? 'w-9 h-9 rounded-[40px] justify-center p-2'
													: 'w-[230px] h-[36px] px-2 rounded-[40px] text-left'
											} text-secondary-text hover:bg-tertiary-background`}
										>
											{/* Icon with consistent positioning */}
											<div
												className={`absolute top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center ${
													collapsed ? 'left-1/2 -translate-x-1/2' : 'left-2'
												}`}
											>
												<span className="text-primary-text">
													<Search className="w-[20px] h-[20px]" strokeWidth={1.75} />
												</span>
											</div>

											{/* Text label with smooth width and opacity transition */}
											<div
												className={`overflow-hidden transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] ${
													collapsed ? 'w-0 opacity-0' : 'w-auto opacity-100 ml-[30px]'
												}`}
											>
												<span className="text-ui whitespace-nowrap">Search chats</span>
											</div>
										</button>
									)}
								</>
							) : (
								<div className="w-[230px] h-[36px] rounded-[40px] border border-tertiary-background bg-white">
									<input
										type="text"
										value={searchTerm}
										onChange={handleSearchInputChange}
										onKeyDown={handleSearchKeyPress}
										placeholder="Search conversations..."
										className="w-full h-full text-ui text-secondary-text placeholder-gray-400 border-none outline-none bg-transparent p-2 pr-8"
										autoFocus
									/>
									<div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1 gap-[10px]">
										<button
											onClick={handleSearchSubmit}
											disabled={!searchTerm.trim() || isSearching}
											className="disabled:opacity-50 disabled:cursor-not-allowed"
											title="Search conversations"
										>
											{isSearching ? (
												<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
											) : (
												<Search
													className="w-5 h-5 text-primary-text hover:text-green-700"
													strokeWidth={1.75}
												/>
											)}
										</button>
										<button
											onClick={() => {
												// Clear any pending search timeout
												if (searchTimeoutRef.current) {
													clearTimeout(searchTimeoutRef.current);
												}
												setShowSearchInput(false);
												setSearchTerm('');
												// Clear search and reload all conversations
												clearSearch();
											}}
											className=""
											title="Cancel search"
										>
											<X
												className="w-5 h-5 text-primary-text hover:text-red-700"
												strokeWidth={1.75}
											/>
										</button>
									</div>
								</div>
							)}
						</div>

						{/* Explore Data - Using Tooltip Component */}
						<div className={`relative ${collapsed ? 'flex justify-center' : ''}`}>
							{collapsed ? (
								<Tooltip text="Explore your data" position="right">
									<button
										onClick={() => onViewSchemaStructure()}
										className={`group relative flex items-center transition-colors duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] ${
											collapsed
												? 'w-9 h-9 rounded-[40px] justify-center p-2'
												: 'w-[230px] h-[36px] px-2 rounded-[40px] text-left'
										} text-secondary-text hover:bg-tertiary-background`}
									>
										{/* Icon with consistent positioning */}
										<div
											className={`absolute top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center ${
												collapsed ? 'left-1/2 -translate-x-1/2' : 'left-2'
											}`}
										>
											<span className="text-primary-text">
												<Table2 className="w-[20px] h-[20px]" strokeWidth={1.75} />
											</span>
										</div>

										{/* Text label with smooth width and opacity transition */}
										<div
											className={`overflow-hidden transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] ${
												collapsed ? 'w-0 opacity-0' : 'w-auto opacity-100 ml-[30px]'
											}`}
										>
											<span className="text-ui whitespace-nowrap">Explore your data</span>
										</div>
									</button>
								</Tooltip>
							) : (
								<button
									onClick={() => onViewSchemaStructure()}
									className={`group relative flex items-center transition-colors duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] ${
										collapsed
											? 'w-9 h-9 rounded-[40px] justify-center p-2'
											: 'w-[230px] h-[36px] px-2 rounded-[40px] text-left'
									} text-secondary-text hover:bg-tertiary-background`}
								>
									{/* Icon with consistent positioning */}
									<div
										className={`absolute top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center ${
											collapsed ? 'left-1/2 -translate-x-1/2' : 'left-2'
										}`}
									>
										<span className="text-primary-text">
											<Table2 className="w-[20px] h-[20px]" strokeWidth={1.75} />
										</span>
									</div>

									{/* Text label with smooth width and opacity transition */}
									<div
										className={`overflow-hidden transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] ${
											collapsed ? 'w-0 opacity-0' : 'w-auto opacity-100 ml-[30px]'
										}`}
									>
										<span className="text-ui whitespace-nowrap">Explore your data</span>
									</div>
								</button>
							)}
						</div>

						{/* 16px spacing after Explore Data button */}
						{!collapsed && <div className="h-6"></div>}

						{/* New Project - Only show when expanded */}
						{!collapsed && (
							<div className={`relative ${collapsed ? 'flex justify-center' : ''}`}>
								{!showNewProjectInput ? (
									<button
										onClick={() => setShowNewProjectInput(true)}
										disabled={loadingProjects}
										className={`group relative flex items-center transition-colors duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] ${
											collapsed
												? 'w-9 h-9 rounded-[40px] justify-center p-2'
												: 'w-[230px] h-[36px] px-2 rounded-[40px] text-left'
										} text-secondary-text hover:bg-tertiary-background disabled:opacity-50 disabled:cursor-not-allowed`}
									>
										{/* Icon with consistent positioning */}
										<div
											className={`absolute top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center ${
												collapsed ? 'left-1/2 -translate-x-1/2' : 'left-2'
											}`}
										>
											<span className="text-primary-text">
												<FolderPlus className="w-[20px] h-[20px]" strokeWidth={1.75} />
											</span>
										</div>

										{/* Text label with smooth width and opacity transition */}
										<div
											className={`overflow-hidden transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] ${
												collapsed ? 'w-0 opacity-0' : 'w-auto opacity-100 ml-[30px]'
											}`}
										>
											<span className="text-ui whitespace-nowrap">New project</span>
										</div>
									</button>
								) : (
									<div className="w-[230px] h-[36px]  rounded-[40px] border border-tertiary-background bg-white">
										<input
											type="text"
											value={newProjectName}
											onChange={(e) => setNewProjectName(e.target.value)}
											onKeyPress={(e) => e.key === 'Enter' && handleCreateProject()}
											placeholder="Project name"
											disabled={loadingProjects}
											className="w-full h-full text-ui text-secondary-text placeholder-gray-400 border-none outline-none bg-transparent disabled:opacity-50 p-2"
											autoFocus
										/>
										<div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1 gap-[10px]">
											<button
												onClick={handleCreateProject}
												disabled={!newProjectName.trim() || loadingProjects}
												className=" disabled:opacity-50 disabled:cursor-not-allowed"
											>
												{loadingProjects ? (
													<div className="animate-spin rounded-full  "></div>
												) : (
													<Check
														className="w-5 h-5 text-primary-text hover:text-green-700"
														strokeWidth={1.75}
													/>
												)}
											</button>
											<button
												onClick={() => {
													setShowNewProjectInput(false);
													setNewProjectName('');
												}}
												disabled={loadingProjects}
												className="disabled:opacity-50 disabled:cursor-not-allowed"
												title="Cancel new project"
											>
												<X
													className="w-5 h-5 text-primary-text hover:text-red-700"
													strokeWidth={1.75}
												/>
											</button>
										</div>
									</div>
								)}
							</div>
						)}

						{/* Additional actions - Only show when expanded */}
						{!collapsed && (
							<>
								{/* Dynamic Projects Loop */}
								{loadingProjects ? (
									<div className="w-[230px] h-[36px] px-2 py-2 flex items-center justify-center">
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
									</div>
								) : projectsLoaded && visibleProjects.length === 0 ? null : (
									projectsToShow.map((project) => {
										const isActive = activeProject?.projectId === project.projectId;
										return (
											<div key={project.projectId}>
												<div className="relative group">
													<button
														onClick={() => handleProjectSelect(project)}
														onDragOver={(e) => handleProjectDragOver(e, project.projectId)}
														onDragLeave={handleProjectDragLeave}
														onDrop={(e) => handleProjectDrop(e, project.projectId)}
														className={`relative flex items-center transition-colors duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] ${
															collapsed
																? 'w-9 h-9 rounded-[40px] justify-center p-2'
																: 'w-[230px] h-[36px] px-2 rounded-[40px] text-left'
														} ${
															isActive
																? 'bg-tertiary-background text-primary-text'
																: dragOverProjectId === project.projectId
																? 'bg-[#E8F2FF] border-2 border-dashed border-blue-400'
																: 'text-secondary-text hover:bg-tertiary-background'
														} ${isAssigning ? 'opacity-50 cursor-not-allowed' : ''}`}
													>
														{/* Icon with consistent positioning */}
														<div
															className={`absolute top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center ${
																collapsed ? 'left-1/2 -translate-x-1/2' : 'left-2'
															}`}
														>
															<span className="text-primary-text">
																{isActive ? (
																	<FolderOpen
																		className="w-[20px] h-[20px]"
																		strokeWidth={1.75}
																	/>
																) : (
																	<FolderClosed
																		className="w-[20px] h-[20px]"
																		strokeWidth={1.75}
																	/>
																)}
															</span>
														</div>

														{/* Text label with smooth width and opacity transition */}
														<div
															className={`overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] ${
																collapsed
																	? 'w-0 opacity-0'
																	: 'w-auto opacity-100 ml-[30px]'
															}`}
														>
															<span className="text-ui whitespace-nowrap">
																{project.projectName}
															</span>
														</div>
													</button>

													{/* 3-dots menu - only show when not collapsed */}
													{!collapsed && (
														<>
															{/* 3-dots button */}
															<button
																onClick={(e) =>
																	handleProjectMenuClick(e, project.projectId)
																}
																className="absolute right-2 top-1/2 transform -translate-y-1/2 opacity-100 p-1 hover:bg-gray-200 rounded transition-opacity"
																title="Project options"
																data-menu-trigger
															>
																<MoreVertical
																	className="w-4 h-4 text-gray-600"
																	strokeWidth={1.75}
																/>
															</button>

															{/* Dropdown menu */}
															{showProjectMenu === project.projectId && (
																<div className="project-menu absolute right-0 top-full mt-1 w-48 z-50 rounded-[24px] shadow-elev-3-popover border border-tertiary-background bg-white p-2">
																	<button
																		onClick={() =>
																			handleRenameProject(
																				project.projectId,
																				project.projectName
																			)
																		}
																		className="w-full text-left text-secondary-text hover:bg-gray-100 flex items-center gap-3 px-3 py-2 text-ui rounded-[36px]"
																	>
																		<Edit2
																			className="w-4 h-4 mr-2"
																			strokeWidth={1.75}
																		/>
																		Rename
																	</button>
																	<button
																		onClick={() =>
																			handleDeleteProject(
																				project.projectId,
																				project.projectName
																			)
																		}
																		className="w-full text-left text-red-600 hover:bg-red-50 flex items-center gap-3 px-3 py-2 text-ui rounded-[36px]"
																	>
																		<Trash2
																			className="w-4 h-4 mr-2"
																			strokeWidth={1.75}
																		/>
																		Delete
																	</button>
																</div>
															)}
														</>
													)}
												</div>

												{/* Show project conversations when folder is opened */}
												{isActive && !collapsed && (
													<div className="ml-6 mt-1 space-y-1">
														{loadingProjectConversations ? (
															<div className="flex justify-center items-center py-2">
																<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
															</div>
														) : activeProjectConversationList.length === 0 ? (
															<div className="text-center py-2 text-meta text-neutral-500">
																No conversations in this project
															</div>
														) : (
															<>
																{/* Show conversations based on showAllProjectConversations state */}
																{(showAllProjectConversations
																	? activeProjectConversationList
																	: activeProjectConversationList.slice(0, 5)
																).map((conversation: any) => {
																	const conversationId =
																		'conversation_id' in conversation
																			? conversation.conversation_id
																			: conversation.id;
																	return (
																		<div
																			key={conversationId}
																			className="relative group"
																		>
																			<button
																				onClick={() =>
																					handleSelectProjectConversation(
																						conversationId
																					)
																				}
																				draggable
																				onDragStart={(e) =>
																					handleConversationDragStart(
																						e,
																						conversationId
																					)
																				}
																				onDragEnd={handleConversationDragEnd}
																				className={`w-[206px] h-[32px] text-left px-2 py-1 text-ui transition-colors duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] rounded-[20px] ${
																					conversationId ===
																						activeProjectConversationId &&
																					conversationSource === 'project'
																						? 'bg-[#edf1f8] text-secondary-text'
																						: draggedConversationId ===
																						  conversationId
																						? 'bg-[#F0F8FF] border-2 border-dashed border-blue-400 opacity-50'
																						: 'text-gray-600 hover:bg-tertiary-background'
																				} ${
																					isAssigning
																						? 'opacity-50 cursor-not-allowed'
																						: ''
																				}`}
																			>
																				<div className="truncate">
																					{conversation.title}
																				</div>
																			</button>

																			{/* 3-dots menu for project conversations - only show when conversation is from project source */}
																			{conversationSource === 'project' &&
																				conversationId ===
																					activeProjectConversationId && (
																					<>
																						{/* 3-dots button */}
																						<button
																							onClick={(e) =>
																								handleConversationMenuClick(
																									e,
																									conversationId
																								)
																							}
																							className="absolute right-2 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-opacity"
																							title="Conversation options"
																							data-menu-trigger
																						>
																							<MoreVertical
																								className="w-3 h-3 text-gray-600"
																								strokeWidth={1.75}
																							/>
																						</button>

																						{/* Dropdown menu */}
																						{showConversationMenu ===
																							conversationId && (
																							<div className="conversation-menu absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-[36px] shadow-lg z-50">
																								<button
																									onClick={() =>
																										handleDeleteConversation(
																											conversationId,
																											conversation.title
																										)
																									}
																									className="w-full text-left text-red-600 hover:bg-red-50 flex items-center gap-3 px-3 py-2 text-ui rounded-[36px]"
																								>
																									<Trash2
																										className="w-4 h-4 mr-2"
																										strokeWidth={
																											1.75
																										}
																									/>
																									Delete
																								</button>
																							</div>
																						)}
																					</>
																				)}
																		</div>
																	);
																})}

																{/* Show "See All" or "See Less" button based on state */}
																{activeProjectConversationList.length > 5 && (
																	<button
																		onClick={
																			showAllProjectConversations
																				? handleShowLessProjectConversations
																				: handleShowAllProjectConversations
																		}
																		className="w-[206px] h-[32px] text-left px-2 py-1 text-xs text-gray-500 hover:text-secondary-text hover:bg-tertiary-background transition-colors duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] rounded-[20px]"
																	>
																		{showAllProjectConversations
																			? `See Less (${activeProjectConversationList.length})`
																			: `See All (${activeProjectConversationList.length})`}
																	</button>
																)}
															</>
														)}
													</div>
												)}
											</div>
										);
									})
								)}

								{/* Show more/less button for projects - only show one at a time */}
								{!collapsed && (
									<>
										{/* Show "Show more" when there are more projects to load */}
										{hasMoreProjects && (
											<button
												onClick={handleShowMoreProjects}
												disabled={loadingProjects}
												className={`group relative flex items-center transition-colors duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] ${
													collapsed
														? 'w-9 h-9 rounded-[40px] justify-center p-2'
														: 'w-[230px] h-[36px] px-2 rounded-[40px] text-left'
												} text-secondary-text hover:bg-tertiary-background disabled:opacity-50 disabled:cursor-not-allowed`}
											>
												{/* Icon with consistent positioning */}
												<div
													className={`absolute top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center ${
														collapsed ? 'left-1/2 -translate-x-1/2' : 'left-2'
													}`}
												>
													<span className="text-primary-text">
														{loadingProjects ? (
															<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
														) : (
															<MoreHorizontal
																className="w-[20px] h-[20px]"
																strokeWidth={1.75}
															/>
														)}
													</span>
												</div>

												{/* Text label with smooth width and opacity transition */}
												<div
													className={`overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] ${
														collapsed ? 'w-0 opacity-0' : 'w-auto opacity-100 ml-[30px]'
													}`}
												>
													<span className="text-ui whitespace-nowrap">Show more</span>
												</div>
											</button>
										)}

										{/* Show "Show less" when all projects are visible (more than 2) */}
										{!hasMoreProjects && visibleProjects.length > 2 && (
											<button
												onClick={handleShowLessProjects}
												disabled={loadingProjects}
												className={`group relative flex items-center transition-colors duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] ${
													collapsed
														? 'w-9 h-9 rounded-[40px] justify-center p-2'
														: 'w-[230px] h-[36px] px-2 rounded-[40px] text-left'
												} text-secondary-text hover:bg-tertiary-background disabled:opacity-50 disabled:cursor-not-allowed`}
											>
												{/* Icon with consistent positioning */}
												<div
													className={`absolute top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center ${
														collapsed ? 'left-1/2 -translate-x-1/2' : 'left-2'
													}`}
												>
													<span className="text-primary-text">
														{loadingProjects ? (
															<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
														) : (
															<MoreHorizontal
																className="w-[20px] h-[20px]"
																strokeWidth={1.75}
															/>
														)}
													</span>
												</div>

												{/* Text label with smooth width and opacity transition */}
												<div
													className={`overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] ${
														collapsed ? 'w-0 opacity-0' : 'w-auto opacity-100 ml-[30px]'
													}`}
												>
													<span className="text-ui whitespace-nowrap">Show less</span>
												</div>
											</button>
										)}
									</>
								)}

								{/* 16px spacing after projects */}
								{!collapsed && <div className="h-4"></div>}
							</>
						)}
					</div>

					{/* Chat History - Smooth expand/collapse transitions */}
					<div className="flex-1 flex flex-col overflow-hidden" style={{ paddingBottom: '32px' }}>
						<h3
							className={`h-9 text-light-grey text-left p-2 transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] ${
								collapsed ? 'opacity-0 transform -translate-y-1' : 'opacity-100 transform translate-y-0'
							}`}
						>
							<span className="text-ui whitespace-nowrap ml-3">Chats</span>
						</h3>
						<div className={`flex-1 ${collapsed ? 'overflow-hidden' : 'overflow-auto custom-scrollbar'}`}>
							<div
								className={`transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] flex flex-col items-center px-3 ${
									collapsed
										? 'opacity-0 transform -translate-y-1'
										: 'opacity-100 transform translate-y-0'
								}`}
							>
								{sortedConversations.length === 0 ? (
									loading || isSearching ? (
										<div className="flex justify-center items-center p-3">
											<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
										</div>
									) : (
										<div className="text-center py-2 text-meta text-neutral-500">
											{searchTerm ? 'No conversations found' : 'No conversations yet'}
										</div>
									)
								) : (
									<>
										{/* Search loading indicator */}
										{isSearching && sortedConversations.length > 0 && (
											<div className="flex justify-center items-center py-2">
												<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
											</div>
										)}

										<div
											className="w-full pr-2 custom-scrollbar flex-1 overflow-auto"
											// style={{
											// 	height: '50vh',
											// 	maxHeight: '55vh',
											// 	overflowY: 'auto',
											// 	overflowX: 'hidden',
											// }}
											onScroll={handleScroll}
										>
											<div className="space-y-2 w-full" style={{ minHeight: '45vh' }}>
												{/* Conversations */}
												{sortedConversations.map((conversation: any) => {
													const conversationId =
														'conversation_id' in conversation
															? conversation.conversation_id
															: conversation.id;
													return (
														<div key={conversationId} className="relative group">
															{!collapsed && (
																<Tooltip text={conversation.title} position="right">
																	<button
																		onClick={() =>
																			handleSelectConversation(conversationId)
																		}
																		draggable
																		onDragStart={(e) =>
																			handleConversationDragStart(e, conversationId)
																		}
																		onDragEnd={handleConversationDragEnd}
																		className={`w-[206px] h-[36px] text-left px-2 py-2 text-ui transition-colors duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] rounded-[40px] ${
																			conversationId === activeConversationId &&
																			(conversationSource === 'main' || conversationSource === null)
																				? 'bg-[#edf1f8] text-secondary-text'
																				: draggedConversationId === conversationId
																				? 'bg-[#F0F8FF] border-2 border-dashed border-blue-400 opacity-50'
																				: 'text-secondary-text hover:bg-tertiary-background'
																		} 
																		${
																	collapsed
																		? 'opacity-0 transform -translate-y-1'
																		: 'opacity-100 transform translate-y-0'
																}
																${
																			isAssigning
																				? 'opacity-50 cursor-not-allowed'
																				: ''
																		}`}
																	>
																		<div
																	className={`overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] ${
																		collapsed
																			? 'w-0 opacity-0'
																			: 'w-auto opacity-100'
																	}`}
																>
																			<span className="truncate block">
																				{conversation.title}
																			</span>
																		</div>
																	</button>
																</Tooltip>
															)}

															{/* 3-dots menu for conversations - only show when not collapsed and conversation is from main source */}
															{!collapsed &&
																conversationSource === 'main' &&
																conversationId === activeConversationId && (
																	<>
																		{/* 3-dots button */}
																		<button
																			onClick={(e) =>
																				handleConversationMenuClick(
																					e,
																					conversationId
																				)
																			}
																			className="absolute right-2 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-opacity"
																			title="Conversation options"
																			data-menu-trigger
																		>
																			<MoreVertical
																				className="w-4 h-4 text-gray-600"
																				strokeWidth={1.75}
																			/>
																		</button>

																		{/* Dropdown menu */}
																		{showConversationMenu === conversationId && (
																			<div className="conversation-menu absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-[36px] shadow-lg z-50">
																				<button
																					onClick={() =>
																						handleDeleteConversation(
																							conversationId,
																							conversation.title
																						)
																					}
																					className="w-full gap-3 px-3 py-2 text-ui rounded-[36px] text-left text-red-600 hover:bg-red-50 flex items-center"
																				>
																					<Trash2
																						className="w-4 h-4 mr-2"
																						strokeWidth={1.75}
																					/>
																					Delete
																				</button>
																			</div>
																		)}
																	</>
																)}
														</div>
													);
												})}

												{/* Loading indicator when loading more conversations */}
												{isLoadingMore && hasMore && (
													<div className="flex justify-center items-center p-3">
														<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
													</div>
												)}
											</div>
										</div>
									</>
								)}
							</div>
						</div>
					</div>

					{/* Drag and Drop Overlay */}
					{draggedConversationId && (
						<div className="fixed inset-0 pointer-events-none z-[100] bg-black bg-opacity-10 flex items-center justify-center">
							<div className="bg-white rounded-lg p-4 shadow-lg border-2 border-blue-400">
								<div className="flex items-center space-x-2">
									<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
									<span className="text-sm font-medium text-secondary-text">
										Drop conversation on a project to assign it
									</span>
								</div>
							</div>
						</div>
					)}

					{/* Delete Confirmation Dialog */}
					{deleteConfirmDialog && (
						<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
							<div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
								<h3 className="text-body text-gray-900 pb-4">
									Delete {deleteConfirmDialog.type === 'project' ? 'Project' : 'Conversation'}
								</h3>
								<p className="text-ui text-gray-600 mb-6">
									Are you sure you want to delete "{deleteConfirmDialog.name}"? This action cannot be
									undone.
								</p>
								<div className="flex justify-end space-x-3">
									<button
										onClick={() => setDeleteConfirmDialog(null)}
										className="px-4 py-2 text-ui text-secondary-text bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
									>
										Cancel
									</button>
									<button
										onClick={confirmDelete}
										className="px-4 py-2 text-ui text-white bg-red-600 hover:bg-red-700 rounded-full transition-colors"
									>
										Delete
									</button>
								</div>
							</div>
						</div>
					)}

					{/* Inline Rename Dialog */}
					{editingProject && (
						<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
							<div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
								<h3 className="text-body  text-primary-text pb-4">Rename Project</h3>
								<input
									type="text"
									value={tempProjectName}
									onChange={(e) => setTempProjectName(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											handleRenameSubmit();
										} else if (e.key === 'Escape') {
											handleRenameCancel();
										}
									}}
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
									placeholder="Enter project name"
									autoFocus
								/>
								<div className="flex justify-end space-x-3 mt-6">
									<button
										onClick={handleRenameCancel}
										className="px-4 py-2 text-ui text-secondary-text bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
									>
										Cancel
									</button>
									<button
										onClick={handleRenameSubmit}
										disabled={!tempProjectName.trim()}
										className="px-4 py-2 text-ui text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-full transition-colors"
									>
										Save
									</button>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</>
	);
}

export default AIConversation;
